import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Icon from '../components/Icon';
import { createHousehold, discoverNearbyStores, rebuildWeek } from '../lib/api';
import { useAppState } from '../components/StateProvider';
import type { DiscoveredStore, HouseholdLocation, StoreDiscoveryResult } from '../types';

const manualStoreChoices = ['Costco','T&T','Save-On-Foods','Superstore','Walmart'];

function storeKey(store: DiscoveredStore) {
  return store.providerPlaceId || `${store.name}|${store.formattedAddress || ''}`;
}

function safeNext(value: string | null) {
  if (!value || !value.startsWith('/') || value.startsWith('//')) return '/';
  return value;
}

export default function Onboarding() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const next = safeNext(searchParams.get('next'));
  const { reload } = useAppState();
  const [name, setName] = useState('Our Household');
  const [ownerName, setOwnerName] = useState('');
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [budget, setBudget] = useState(220);
  const [shopDays, setShopDays] = useState(2);
  const [manualStores, setManualStores] = useState<string[]>(['Costco','T&T']);
  const [address, setAddress] = useState('');
  const [location, setLocation] = useState<HouseholdLocation | null>(null);
  const [nearbyStores, setNearbyStores] = useState<DiscoveredStore[]>([]);
  const [selectedStores, setSelectedStores] = useState<string[]>([]);
  const [findingStores, setFindingStores] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const householdPeople = useMemo(() => adults + children, [adults, children]);

  function toggleManualStore(store: string) {
    setManualStores((current) => current.includes(store) ? current.filter((x) => x !== store) : [...current, store]);
  }

  function toggleNearbyStore(store: DiscoveredStore) {
    const key = storeKey(store);
    setSelectedStores((current) => current.includes(key) ? current.filter((x) => x !== key) : [...current, key]);
  }

  function applyDiscovery(result: StoreDiscoveryResult) {
    const nextLocation: HouseholdLocation = { ...result.location, radiusKm: result.radiusKm };
    setLocation(nextLocation);
    setNearbyStores(result.stores);
    const defaults = result.stores.filter((s) => s.recommended).slice(0, 5).map(storeKey);
    setSelectedStores(defaults.length ? defaults : result.stores.slice(0, 4).map(storeKey));
    setLocationError('');
  }

  async function searchAddress() {
    if (!address.trim()) { setLocationError('Enter an address, city, or postal code.'); return; }
    setFindingStores(true); setLocationError('');
    try {
      applyDiscovery(await discoverNearbyStores({ address: address.trim(), regionCode: 'CA' }));
    } catch (e) {
      console.error(e);
      setLocationError(e instanceof Error ? e.message : 'Weekstead could not find nearby stores.');
    } finally { setFindingStores(false); }
  }

  async function useMyLocation() {
    if (!navigator.geolocation) { setLocationError('Location access is not supported on this device.'); return; }
    setFindingStores(true); setLocationError('');
    navigator.geolocation.getCurrentPosition(async (position) => {
      try {
        applyDiscovery(await discoverNearbyStores({ latitude: position.coords.latitude, longitude: position.coords.longitude, regionCode: 'CA' }));
      } catch (e) {
        console.error(e);
        setLocationError(e instanceof Error ? e.message : 'Weekstead could not find nearby stores.');
      } finally { setFindingStores(false); }
    }, (geoError) => {
      setFindingStores(false);
      setLocationError(geoError.code === geoError.PERMISSION_DENIED ? 'Location permission was declined. You can enter a postal code instead.' : 'Your location could not be determined. Try entering a postal code.');
    }, { enableHighAccuracy: false, timeout: 12000, maximumAge: 300000 });
  }

  async function finish() {
    const chosenNearby = nearbyStores.filter((s) => selectedStores.includes(storeKey(s)));
    const storesForHousehold = nearbyStores.length ? chosenNearby : manualStores;
    if (!name.trim() || storesForHousehold.length === 0) { setError('Name the household and choose at least one store.'); return; }
    setBusy(true); setError('');
    try {
      const householdId = await createHousehold({
        name: name.trim(),
        ownerName: ownerName.trim(),
        weeklyBudget: budget,
        shopDaysPerWeek: shopDays,
        adults,
        children,
        stores: storesForHousehold,
        location,
      });
      await rebuildWeek(householdId, 'Create our first Weekstead food plan using our nearby stores');
      await reload();
      navigate(next, { replace: true });
    } catch (e) {
      console.error(e);
      setError(e instanceof Error ? e.message : 'Weekstead could not create the household.');
    } finally { setBusy(false); }
  }

  const selectedCount = nearbyStores.length ? selectedStores.length : manualStores.length;

  return <div className="onboarding-page"><div className="onboarding-card">
    <div className="onboarding-brand"><img src="/weekstead-icon.png" alt="" /><div><span className="eyebrow">Weekstead setup</span><h1>Tell us how your household actually eats.</h1><p>This becomes the starting model. Weekstead will learn and adjust as you shop, cook and waste less.</p></div></div>

    {next.startsWith('/share/') && <div className="location-result"><Icon name="check" /><div><strong>Your shared recipe is waiting.</strong><span>Finish household setup and Weekstead will bring you back to the private recipe so it can check your household food rules before saving.</span></div></div>}

    <div className="onboarding-grid"><label>Household name<input value={name} onChange={(e) => setName(e.target.value)} /></label><label>Your name<input value={ownerName} onChange={(e) => setOwnerName(e.target.value)} placeholder="Optional" /></label><label>Adults<input type="number" min="1" max="12" value={adults} onChange={(e) => setAdults(Math.max(1, Number(e.target.value) || 1))} /></label><label>Children<input type="number" min="0" max="12" value={children} onChange={(e) => setChildren(Math.max(0, Number(e.target.value) || 0))} /></label><label>Weekly grocery target ($CAD)<input type="number" min="0" value={budget} onChange={(e) => setBudget(Math.max(0, Number(e.target.value) || 0))} /></label><label>Shopping runs per week<input type="number" min="1" max="7" value={shopDays} onChange={(e) => setShopDays(Math.min(7, Math.max(1, Number(e.target.value) || 1)))} /></label></div>

    <section className="location-setup">
      <div className="location-copy"><span className="eyebrow">Find the stores around home</span><h2>Let Weekstead build your grocery radius.</h2><p>Use your device location or enter an address/postal code. Weekstead stores only a coarse home anchor and location label — not the raw street address you type here.</p></div>
      <div className="location-actions"><button className="outline" disabled={findingStores} onClick={() => void useMyLocation()}><Icon name="store" /> {findingStores ? 'Finding stores…' : 'Use my location'}</button><div className="address-search"><input value={address} onChange={(e) => setAddress(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && void searchAddress()} placeholder="Postal code, city, or address" /><button className="outline" disabled={findingStores} onClick={() => void searchAddress()}>Find stores</button></div></div>
      {locationError && <p className="auth-error location-error">{locationError}</p>}
      {location && <div className="location-result"><Icon name="check" /><div><strong>{location.label}</strong><span>Weekstead found a practical grocery radius of about {location.radiusKm} km.</span></div></div>}
    </section>

    {nearbyStores.length > 0 ? <div className="store-chooser discovered"><span className="eyebrow">Recommended nearby stores</span><p className="store-helper">Choose the stores you would realistically use. Weekstead can learn which ones deserve priority later from receipts, prices and shopping history.</p><div className="discovered-store-grid">{nearbyStores.map((store) => {
      const selected = selectedStores.includes(storeKey(store));
      return <button key={storeKey(store)} className={selected ? 'selected discovered-store' : 'discovered-store'} onClick={() => toggleNearbyStore(store)}><span className="store-select-icon"><Icon name={selected ? 'check' : 'store'} /></span><span className="store-result-copy"><strong>{store.name}</strong><small>{store.distanceKm?.toFixed(1)} km{store.majorChain ? ' · major grocery chain' : ''}</small><small>{store.formattedAddress}</small></span></button>;
    })}</div></div> : <div className="store-chooser"><span className="eyebrow">Where you normally shop</span><p className="store-helper">You can continue manually if you prefer not to share a location.</p><div>{manualStoreChoices.map((store) => <button key={store} className={manualStores.includes(store) ? 'selected' : ''} onClick={() => toggleManualStore(store)}><Icon name={manualStores.includes(store) ? 'check' : 'store'} /> {store}</button>)}</div></div>}

    <div className="onboarding-summary"><strong>{householdPeople} people</strong><span>·</span><strong>{shopDays} run{shopDays === 1 ? '' : 's'}/week</strong><span>·</span><strong>{selectedCount} selected store{selectedCount === 1 ? '' : 's'}</strong>{location && <><span>·</span><strong>~{location.radiusKm} km grocery area</strong></>}</div>
    {error && <p className="auth-error">{error}</p>}
    <button className="primary onboarding-submit" disabled={busy} onClick={() => void finish()}>{busy ? 'Creating your household…' : 'Create my Weekstead household'} <Icon name="arrow" /></button>
  </div></div>;
}
